
import {
  ComponentPublicInstance,
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import MessengerHeader from "@/components/MessengerHeader.vue";
import MessengerEditor from "@/components/MessengerEditor.vue";
import MessengerMessageList from "@/components/MessengerMessageList.vue";
import { useEventBus, useNow } from "@vueuse/core";
import {
  MessageListScrollNotifyBus,
  MessageListScrollNotifyBusKey,
} from "@/types/MessageListScrollNotifyBus";
import PopTransition from "@/components/PopTransition.vue";
import { useConversation } from "@/composables/conversation";
import MessengerChannelPicker from "@/components/MessengerChannelPicker.vue";
import { DialogWrapper } from "vue3-promise-dialog";
import { useChannelSelector } from "@/composables/channelSelector";
import MessengerStartChatForm from "@/components/MessengerStartChatForm.vue";
import MessengerConversationEnded from "@/components/MessengerConversationEnded.vue";
import MessengerConnectionStatus from "@/components/MessengerConnectionStatus.vue";
import { useApp } from "@/composables/use-app";
import { useFileUploader } from "@/composables/fileUploader";
import { css } from "@emotion/css";
import { useWidgetTheme } from "@/composables/widgetTheme";
import { TinyColor } from "@ctrl/tinycolor";
import Dropzone from "@/components/Dropzone.vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "Messenger",
  components: {
    Dropzone,
    MessengerConnectionStatus,
    MessengerConversationEnded,
    MessengerStartChatForm,
    MessengerChannelPicker,
    PopTransition,
    MessengerMessageList,
    MessengerEditor,
    MessengerHeader,
    DialogWrapper,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { useMobileLayout, isPreviewMode } = useApp();
    const now = useNow();
    const { theme } = useWidgetTheme();

    const {
      conversation,
      messages,
      isTransferredAndWithinNewMessageThreshold,
    } = useConversation();
    const messageList = ref<ComponentPublicInstance<HTMLDivElement>>();
    const { selectedChannel } = useChannelSelector();
    const { t } = useI18n();
    const { uploadFiles: doUploadFiles } = useFileUploader(t);
    const dropZoneTriggered = ref(false);

    const isConversationStarted = computed(() => conversation.value?.isStarted);
    const isConversationTransferred = computed(
      () => conversation.value?.isTransferred
    );
    const isConversationEnded = computed(() => conversation.value?.isEnded);

    const scrollNotifier = useEventBus<MessageListScrollNotifyBus>(
      MessageListScrollNotifyBusKey
    );

    const hasSelectedChannel = computed(() => !!selectedChannel.value);

    const showEditor = computed(() => {
      if (conversation.value?.isEnded) {
        return false;
      }

      if (conversation.value?.isTransferred) {
        return isTransferredAndWithinNewMessageThreshold.value;
      }

      return true;
    });

    const unsubscribeScrollNotifier = scrollNotifier.on((msg) => {
      if (!messageList.value?.$el) {
        console.warn("Could not scroll to bottom; element missing");
        return;
      }

      const el = messageList.value.$el;
      let shouldScroll = true;

      if (msg?.scrollOnlyIfAlreadyInBottom === true) {
        shouldScroll = false;
      }

      if (shouldScroll) {
        nextTick(() => {
          //console.log(el.scrollHeight)
          el.scrollTop = el.scrollHeight;
        });
      }
    });

    const minimize = () => {
      emit("minimize");
    };

    const onFilesDropped = async (files: File[]) => {
      doUploadFiles(files);
    };

    onBeforeUnmount(() => {
      unsubscribeScrollNotifier();
    });

    const backgroundClass = computed(() => {
      const bgColorInfo = new TinyColor(theme.value?.mainBackgroundColor);
      const darkened = bgColorInfo.darken(5);

      return css`
        background-color: ${theme.value?.mainBackgroundColor};
        border-color: #${darkened.toHex(false)};
      `;
    });

    const dropzoneClass = computed(() => {
      if (!dropZoneTriggered.value) {
        return "";
      }

      const bgColorInfo = new TinyColor(theme.value?.mainBackgroundColor);
      const darkened = bgColorInfo.darken(10);

      return css`
        @keyframes glow {
          0% {
            box-shadow: 0 0 4px 0.1em #${darkened.toHex()};
          }

          100% {
            box-shadow: 0 0 4px 0.4em #${darkened.toHex()};
          }
        }

        -webkit-animation: glow 0.75s infinite alternate;
        animation: glow 0.75s infinite alternate;
        animation-fill-mode: forwards;
      `;
    });

    return {
      minimize,
      messageList,
      isConversationStarted,
      isConversationTransferred,
      isConversationEnded,
      hasSelectedChannel,
      showEditor,
      isMobile: useMobileLayout,
      isPreviewMode,
      backgroundClass,
      dropzoneClass,
      onFilesDropped,
      dropZoneTriggered,
    };
  },
});
